import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fmtDate, fmtDateTime, type TimesheetStatus } from '@em/shared';
import { api, invalidateAttendance, usePeriod, useSettings } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { deadlineText } from '../../components/format';
import { PromptDialog } from '../../components/PromptDialog';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyState, ErrorAlert, Hours, PageHeader, PeriodBadge, QueryState, Tabs, TimesheetBadge } from '../../components/ui';

type Filter = 'all' | 'needs_attention' | 'ready' | 'approved' | 'other';
const FILTERS: { value: Filter; label: string; match: (s: TimesheetStatus) => boolean }[] = [
  { value: 'all', label: 'All', match: () => true },
  { value: 'needs_attention', label: 'Needs attention', match: (s) => s === 'needs_attention' || s === 'rejected' },
  { value: 'ready', label: 'Ready', match: (s) => s === 'ready' },
  { value: 'approved', label: 'Approved', match: (s) => s === 'approved' || s === 'locked' || s === 'exported' },
  { value: 'other', label: 'Open', match: (s) => s === 'open' },
];

export function PeriodDetailPage() {
  const { id = '' } = useParams();
  const q = usePeriod(id);
  const { can } = useAuth();
  const toast = useToast();
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<Filter>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ title: string; error: unknown } | null>(null);
  const [bulkResult, setBulkResult] = useState<{ approved: number; skipped: { name: string; reason: string }[] } | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  const timesheets = q.data?.timesheets ?? [];
  const ready = useMemo(() => timesheets.filter((t) => t.status === 'ready'), [timesheets]);
  const visible = timesheets.filter((t) => FILTERS.find((f) => f.value === filter)!.match(t.status));
  const allReadySelected = ready.length > 0 && ready.every((t) => selected.has(t.id));

  const run = async (key: string, title: string, fn: () => Promise<void>) => {
    setBusy(key);
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError({ title, error: e });
    } finally {
      setBusy(null);
    }
  };

  const bulkApprove = () =>
    run('approve', 'Approval failed', async () => {
      const ids = [...selected];
      const r = await api.bulkApprove(ids);
      const names = new Map(timesheets.map((t) => [t.id, t.employeeName]));
      setBulkResult({ approved: r.approved.length, skipped: r.skipped.map((s) => ({ name: names.get(s.id) ?? s.id, reason: s.reason })) });
      setSelected(new Set());
      invalidateAttendance();
      if (r.approved.length) toast.success(`${r.approved.length} timesheet${r.approved.length === 1 ? '' : 's'} approved`);
    });

  const lock = () =>
    run('lock', 'The period could not be locked', async () => {
      await api.lockPeriod(id);
      invalidateAttendance();
      toast.success('Period locked', 'Timesheets are now read-only. You can export to MYOB.');
    });

  const recalc = () =>
    run('recalc', 'Recalculation failed', async () => {
      const r = await api.recalculatePeriod(id);
      invalidateAttendance();
      toast.success(`Recalculated ${r.recalculated} timesheet${r.recalculated === 1 ? '' : 's'}`);
    });

  return (
    <QueryState q={q} loadingLabel="Loading pay period">
      {({ period }) => {
        const dl = deadlineText(Math.ceil((Date.parse(period.approvalDeadline) - Date.now()) / 86_400_000));
        return (
          <>
            <PageHeader
              breadcrumb={<Link to="/periods">Pay periods</Link>}
              title={`${fmtDate(period.startDate)} – ${fmtDate(period.endDate)}`}
              subtitle={
                <span className="row">
                  <PeriodBadge status={period.status} />
                  <span>Approval deadline {fmtDateTime(period.approvalDeadline, tz)}</span>
                  {period.status === 'open' && <Badge tone={dl.tone}>{dl.text}</Badge>}
                  {period.lockedByName && <span className="muted">Locked by {period.lockedByName}{period.lockedAt ? ` on ${fmtDateTime(period.lockedAt, tz)}` : ''}</span>}
                </span>
              }
              actions={
                <>
                  {period.status === 'open' && can('payroll_admin') && (
                    <Button onClick={recalc} loading={busy === 'recalc'}>
                      Recalculate
                    </Button>
                  )}
                  {period.status === 'open' && can('payroll_admin') && (
                    <Button variant="primary" onClick={lock} loading={busy === 'lock'}>
                      Lock period
                    </Button>
                  )}
                  {period.status === 'locked' && can('owner') && (
                    <Button variant="danger" onClick={() => setUnlocking(true)}>
                      Unlock
                    </Button>
                  )}
                  {can('payroll_admin') && (
                    <Link className={`btn ${period.status === 'open' ? '' : 'primary'}`} to={`/periods/${id}/export`}>
                      Export to MYOB
                    </Link>
                  )}
                </>
              }
            />

            {actionError && (
              <div style={{ marginBottom: 12 }}>
                <ErrorAlert title={actionError.title} error={actionError.error} />
              </div>
            )}
            {bulkResult && (
              <div style={{ marginBottom: 12 }}>
                <Alert tone={bulkResult.skipped.length ? 'amber' : 'green'} title={`${bulkResult.approved} approved${bulkResult.skipped.length ? `, ${bulkResult.skipped.length} skipped` : ''}`}>
                  {bulkResult.skipped.length > 0 && (
                    <ul>
                      {bulkResult.skipped.map((s, i) => (
                        <li key={i}>
                          <strong>{s.name}</strong>: {s.reason}
                        </li>
                      ))}
                    </ul>
                  )}
                </Alert>
              </div>
            )}

            <div className="grid grid-4" style={{ marginBottom: 16 }}>
              <div className="card stat">
                <div className="label">Timesheets</div>
                <div className="value">{period.counts.total}</div>
              </div>
              <div className="card stat">
                <div className="label">Needs attention</div>
                <div className={`value ${period.counts.needs_attention + period.counts.rejected ? 'red' : ''}`}>{period.counts.needs_attention + period.counts.rejected}</div>
              </div>
              <div className="card stat">
                <div className="label">Ready to approve</div>
                <div className="value">{period.counts.ready}</div>
              </div>
              <div className="card stat">
                <div className="label">Approved</div>
                <div className="value green">{period.counts.approved + period.counts.locked + period.counts.exported}</div>
              </div>
            </div>

            <Card tight>
              <div className="toolbar">
                <Tabs label="Filter timesheets" items={FILTERS.map((f) => ({ value: f.value, label: f.label }))} value={filter} onChange={setFilter} />
                <span className="spacer" />
                {period.status === 'open' && (
                  <>
                    <span className="muted small">{selected.size} selected</span>
                    <Button variant="primary" disabled={selected.size === 0} loading={busy === 'approve'} onClick={bulkApprove}>
                      Approve selected
                    </Button>
                  </>
                )}
              </div>
              {visible.length === 0 ? (
                <EmptyState title="No timesheets here" />
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="check-col">
                          {period.status === 'open' && (
                            <input
                              type="checkbox"
                              aria-label="Select all ready timesheets"
                              checked={allReadySelected}
                              disabled={ready.length === 0}
                              onChange={(e) => setSelected(e.target.checked ? new Set(ready.map((t) => t.id)) : new Set())}
                            />
                          )}
                        </th>
                        <th>Employee</th>
                        <th>Status</th>
                        <th className="num">Paid hours</th>
                        <th className="num">Blocking</th>
                        <th className="num">Review</th>
                        <th>Approved by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((t) => (
                        <tr key={t.id}>
                          <td className="check-col">
                            {period.status === 'open' && (
                              <input
                                type="checkbox"
                                aria-label={`Select ${t.employeeName}`}
                                disabled={t.status !== 'ready'}
                                title={t.status !== 'ready' ? 'Only Ready timesheets can be approved' : undefined}
                                checked={selected.has(t.id)}
                                onChange={(e) =>
                                  setSelected((s) => {
                                    const n = new Set(s);
                                    if (e.target.checked) n.add(t.id);
                                    else n.delete(t.id);
                                    return n;
                                  })
                                }
                              />
                            )}
                          </td>
                          <td className="strong">
                            <Link to={`/timesheets/${t.id}`}>{t.employeeName}</Link> <span className="muted small">{t.employeeNumber}</span>
                          </td>
                          <td>
                            <TimesheetBadge status={t.status} />
                          </td>
                          <td className="num">
                            <Hours value={t.totals.paidHours} />
                          </td>
                          <td className={`num ${t.openBlocking ? 'red strong' : 'muted'}`}>{t.openBlocking}</td>
                          <td className={`num ${t.openReview ? 'amber strong' : 'muted'}`}>{t.openReview}</td>
                          <td>{t.approvedByName ?? <span className="muted">–</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {unlocking && (
              <PromptDialog
                title="Unlock this period"
                description="Unlocking lets timesheets change again. Approvals are kept. It is recorded in the audit log with your reason."
                label="Reason"
                required
                minLength={5}
                danger
                confirmLabel="Unlock period"
                onClose={() => setUnlocking(false)}
                onConfirm={async (reason) => {
                  await api.unlockPeriod(id, reason);
                  invalidateAttendance();
                  toast.success('Period unlocked');
                }}
              />
            )}
          </>
        );
      }}
    </QueryState>
  );
}
